import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Star, StarOff, BookOpen, Copy, PenTool } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import ImageUpload from "../components/ImageUpload";
import { FontSelect } from "../components/FontSelect";
import { refreshCustomFonts } from "../lib/fonts";

export default function Templates() {
  return (
    <div className="p-8 lg:p-10 max-w-6xl">
      <header className="mb-6">
        <p className="text-sm font-medium text-blue-800 uppercase tracking-wider">Design</p>
        <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">
          Certificate Templates & Courses
        </h1>
        <p className="text-slate-500 mt-1">
          Customise the look of your certificates and manage course subjects.
        </p>
      </header>

      <Tabs defaultValue="templates" className="w-full">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <TabsList>
            <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
            <TabsTrigger value="courses" data-testid="tab-courses">Courses & Subjects</TabsTrigger>
          </TabsList>
          <a
            href="/admin/designer"
            data-testid="open-designer-btn"
            className="inline-flex items-center gap-2 text-sm font-medium text-white bg-blue-800 hover:bg-blue-900 rounded-md px-4 h-10 transition-soft"
          >
            Open Template Designer
          </a>
        </div>
        <TabsContent value="templates"><TemplatesTab /></TabsContent>
        <TabsContent value="courses"><CoursesTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- TEMPLATES ---------------- */
function TemplatesTab() {
  const [tpls, setTpls] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/templates");
    setTpls(data);
    setSelected((s) => {
      if (!s) return data[0] || null;
      return data.find((t) => t.id === s.id) || data[0] || null;
    });
  }, []);
  useEffect(() => { load(); refreshCustomFonts(); }, [load]);

  const upd = (k, v) => setSelected((s) => ({ ...s, [k]: v }));
  const updStyle = (k, v) => setSelected((s) => ({ ...s, style: { ...(s.style || {}), [k]: v } }));

  const save = async () => {
    try {
      const { data } = await api.put(`/templates/${selected.id}`, selected);
      toast.success("Template saved");
      setSelected(data);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const create = async () => {
    const { data } = await api.post("/templates", {
      name: "New template",
      heading: "CERTIFICATE OF COMPLETION",
      footer: "Issued by Lovely Institute",
      style: { font_family: "serif", heading_size: 44, body_size: 16,
        font_color: "#0f2a54", accent_color: "#1e40af" },
    });
    await load();
    setSelected(data);
  };
  const del = async () => {
    if (!confirm("Delete this template?")) return;
    await api.delete(`/templates/${selected.id}`);
    setSelected(null);
    load();
  };
  const duplicate = async (t) => {
    const copy = { ...t };
    delete copy.id;
    delete copy.created_at;
    copy.name = `${t.name} (copy)`;
    copy.is_default = false;
    const { data } = await api.post("/templates", copy);
    await load();
    setSelected(data);
    toast.success("Template duplicated");
  };
  const toggleActive = async (t) => {
    await api.put(`/templates/${t.id}`, { ...t, active: t.active === false });
    load();
  };
  const makeDefault = async () => {
    await api.put(`/templates/${selected.id}`, { ...selected, is_default: true });
    toast.success("Set as default template");
    load();
  };

  if (!selected) {
    return (
      <div className="text-center py-16 border border-dashed rounded-xl">
        <p className="text-slate-500 mb-4">No templates yet.</p>
        <Button onClick={create} data-testid="create-tpl-btn">
          <Plus size={16} className="mr-2" /> Create template
        </Button>
      </div>
    );
  }

  const style = selected.style || {};

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
      {/* list */}
      <aside className="rounded-xl border border-slate-200 bg-white p-3">
        <div className="flex items-center justify-between px-2 py-1">
          <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">
            Templates
          </span>
          <button
            data-testid="add-tpl-btn"
            onClick={create}
            className="text-blue-800 hover:text-blue-900"
            title="New template"
          >
            <Plus size={16} />
          </button>
        </div>
        <div className="mt-2 space-y-1">
          {tpls.map((t) => (
            <div
              key={t.id}
              data-testid={`tpl-${t.id}`}
              onClick={() => setSelected(t)}
              className={`w-full text-left px-3 py-2 rounded-md text-sm transition-soft cursor-pointer ${
                selected.id === t.id
                  ? "bg-blue-50 text-blue-900"
                  : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="truncate font-medium">{t.name}</span>
                <div className="flex items-center gap-1 shrink-0">
                  {t.is_default && <Star size={14} className="text-amber-500" fill="currentColor" />}
                  <button
                    data-testid={`tpl-dup-${t.id}`}
                    title="Duplicate"
                    onClick={(e) => { e.stopPropagation(); duplicate(t); }}
                    className="p-1 text-slate-400 hover:text-blue-700"
                  >
                    <Copy size={13} />
                  </button>
                  <a
                    href={`/designer?tpl=${t.id}`}
                    data-testid={`tpl-design-${t.id}`}
                    title="Open in Template Designer"
                    onClick={(e) => e.stopPropagation()}
                    className="p-1 text-slate-400 hover:text-blue-700"
                  >
                    <PenTool size={13} />
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-400">
                <span>v{t.version || 1}</span>
                <span>·</span>
                <span>{t.updated_at ? new Date(t.updated_at).toLocaleDateString() : "—"}</span>
                <button
                  data-testid={`tpl-active-${t.id}`}
                  onClick={(e) => { e.stopPropagation(); toggleActive(t); }}
                  className={`ml-auto px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide ${
                    t.active !== false
                      ? "bg-green-100 text-green-700"
                      : "bg-slate-200 text-slate-500"
                  }`}
                >
                  {t.active !== false ? "Active" : "Inactive"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </aside>

      {/* editor */}
      <section className="rounded-xl border border-slate-200 bg-white">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-500">Editing</div>
            <Input
              className="mt-1 h-9 max-w-sm font-display text-lg font-semibold"
              value={selected.name}
              onChange={(e) => upd("name", e.target.value)}
              data-testid="tpl-name"
            />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={makeDefault} data-testid="tpl-default"
              className="h-9">
              {selected.is_default ? <StarOff size={16} className="mr-2"/> : <Star size={16} className="mr-2"/>}
              {selected.is_default ? "Default" : "Set default"}
            </Button>
            <Button variant="outline" onClick={del} data-testid="tpl-delete" className="h-9 text-red-600">
              <Trash2 size={16} className="mr-2"/> Delete
            </Button>
            <Button onClick={save} data-testid="tpl-save" className="h-9 bg-blue-800 hover:bg-blue-900">
              <Save size={16} className="mr-2"/> Save
            </Button>
          </div>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Heading">
            <Input value={selected.heading || ""} onChange={(e) => upd("heading", e.target.value)}
              className="h-10" data-testid="tpl-heading"/>
          </Field>
          <Field label="Footer text">
            <Input value={selected.footer || ""} onChange={(e) => upd("footer", e.target.value)}
              className="h-10" data-testid="tpl-footer"/>
          </Field>
          <div className="md:col-span-2">
            <Field label="Body text (uses placeholders like {student_name}, {course}, {duration}, {grade})">
              <Textarea rows={3} value={selected.body_text || ""}
                onChange={(e) => upd("body_text", e.target.value)}
                placeholder="Leave empty to use the smart default."
                data-testid="tpl-body"/>
            </Field>
          </div>

          <div className="md:col-span-2 rounded-lg bg-blue-50 border border-blue-100 px-4 py-3 text-xs text-blue-900" data-testid="tpl-assets-note">
            Logos and background images are managed per-layout in the <b>Template Designer</b> (drag, resize, layer, show/hide). The assets below are shared by Format 1 (Certificate) and Format 2 (Marksheet).
          </div>

          <Field label="Authorized Signatory (signature image)">
            <ImageUpload testId="tpl-sig" value={selected.signature}
              onChange={(v)=>upd("signature", v)} aspect="wide"/>
            <label className="flex items-center gap-2 text-xs text-slate-600 mt-1.5">
              <input type="checkbox" data-testid="tpl-sig-enabled"
                checked={selected.signature_enabled !== false}
                onChange={(e) => upd("signature_enabled", e.target.checked)}
                className="w-3.5 h-3.5 accent-blue-800" />
              Enabled on certificates & marksheets
            </label>
          </Field>
          <Field label="Prepared By Signatory (signature image)">
            <ImageUpload testId="tpl-prepared-sig" value={selected.prepared_signature}
              onChange={(v)=>upd("prepared_signature", v)} aspect="wide"/>
            <label className="flex items-center gap-2 text-xs text-slate-600 mt-1.5">
              <input type="checkbox" data-testid="tpl-prepared-sig-enabled"
                checked={selected.prepared_signature_enabled !== false}
                onChange={(e) => upd("prepared_signature_enabled", e.target.checked)}
                className="w-3.5 h-3.5 accent-blue-800" />
              Enabled on certificates & marksheets
            </label>
          </Field>
          <Field label="Stamp / Seal">
            <ImageUpload testId="tpl-stamp" value={selected.stamp} onChange={(v)=>upd("stamp", v)} />
            <label className="flex items-center gap-2 text-xs text-slate-600 mt-1.5">
              <input type="checkbox" data-testid="tpl-stamp-enabled"
                checked={selected.stamp_enabled !== false}
                onChange={(e) => upd("stamp_enabled", e.target.checked)}
                className="w-3.5 h-3.5 accent-blue-800" />
              Enabled on certificates & marksheets
            </label>
          </Field>

          <Field label="Font family">
            <FontSelect value={style.font_family || "serif"} onChange={(v) => updStyle("font_family", v)} testId="tpl-font" className="h-10" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Heading size">
              <Input type="number" value={style.heading_size ?? 44}
                onChange={(e) => updStyle("heading_size", Number(e.target.value))}
                className="h-10" data-testid="tpl-hsize"/>
            </Field>
            <Field label="Body size">
              <Input type="number" value={style.body_size ?? 16}
                onChange={(e) => updStyle("body_size", Number(e.target.value))}
                className="h-10" data-testid="tpl-bsize"/>
            </Field>
          </div>
          <Field label="Text color">
            <Input type="color" value={style.font_color || "#0f2a54"}
              onChange={(e) => updStyle("font_color", e.target.value)}
              className="h-10 w-24" data-testid="tpl-color"/>
          </Field>
          <Field label="Accent color">
            <Input type="color" value={style.accent_color || "#1e40af"}
              onChange={(e) => updStyle("accent_color", e.target.value)}
              className="h-10 w-24" data-testid="tpl-accent"/>
          </Field>
        </div>
      </section>
    </div>
  );
}

/* ---------------- COURSES ---------------- */
function CoursesTab() {
  const [courses, setCourses] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/courses");
    setCourses(data);
    setSelected((s) => {
      if (!s) return data[0] || null;
      return data.find((c) => c.id === s.id) || data[0] || null;
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const upd = (k, v) => setSelected((s) => ({ ...s, [k]: v }));
  const setSubjects = (subs) => setSelected((s) => ({ ...s, subjects: subs }));

  const save = async () => {
    await api.put(`/courses/${selected.id}`, selected);
    toast.success("Course saved");
    load();
  };

  const addSubject = (term) => {
    const subs = [...(selected.subjects || [])];
    subs.push({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, name: "New subject", term });
    setSubjects(subs);
  };
  const removeSubject = (id) => setSubjects((selected.subjects || []).filter((s) => s.id !== id));
  const renameSubject = (id, name) =>
    setSubjects((selected.subjects || []).map((s) => (s.id === id ? { ...s, name } : s)));
  const move = (id, dir) => {
    const subs = [...(selected.subjects || [])];
    const idx = subs.findIndex((s) => s.id === id);
    if (idx < 0) return;
    // find same-term neighbours
    const term = subs[idx].term;
    const sameTerm = subs.filter((s) => s.term === term);
    const pos = sameTerm.findIndex((s) => s.id === id);
    const swapWith = sameTerm[pos + dir];
    if (!swapWith) return;
    const globalSwap = subs.findIndex((s) => s.id === swapWith.id);
    [subs[idx], subs[globalSwap]] = [subs[globalSwap], subs[idx]];
    setSubjects(subs);
  };

  if (!selected) return <div>Loading…</div>;

  const term1 = (selected.subjects || []).filter((s) => s.term === 1);
  const term2 = (selected.subjects || []).filter((s) => s.term === 2);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
      <aside className="rounded-xl border border-slate-200 bg-white p-3">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-medium px-2 py-1">
          Courses
        </div>
        <div className="mt-2 space-y-1">
          {courses.map((c) => (
            <button
              key={c.id}
              data-testid={`course-${c.id}`}
              onClick={() => setSelected(c)}
              className={`w-full text-left px-3 py-2 rounded-md text-sm transition-soft ${
                selected.id === c.id
                  ? "bg-blue-50 text-blue-900 font-medium"
                  : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              <div className="truncate flex items-center gap-2">
                <BookOpen size={14} />
                {c.name}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{c.duration}</div>
            </button>
          ))}
        </div>
      </aside>

      <section className="rounded-xl border border-slate-200 bg-white">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div className="flex-1 grid grid-cols-2 gap-4 max-w-xl">
            <Field label="Course name">
              <Input value={selected.name} onChange={(e) => upd("name", e.target.value)}
                className="h-10" data-testid="course-name"/>
            </Field>
            <Field label="Duration">
              <Input value={selected.duration} onChange={(e) => upd("duration", e.target.value)}
                className="h-10" data-testid="course-duration"/>
            </Field>
            <Field label="Course type (e.g., Diploma / Certificate)">
              <Input value={selected.course_type || ""} onChange={(e) => upd("course_type", e.target.value)}
                className="h-10" data-testid="course-type"/>
            </Field>
            <Field label="Eligibility">
              <Input value={selected.eligibility || ""} onChange={(e) => upd("eligibility", e.target.value)}
                className="h-10" data-testid="course-eligibility"/>
            </Field>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Button onClick={save} data-testid="course-save" className="h-10 bg-blue-800 hover:bg-blue-900">
              <Save size={16} className="mr-2"/> Save
            </Button>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" data-testid="course-active"
                checked={selected.active !== false}
                onChange={(e) => upd("active", e.target.checked)}
                className="w-3.5 h-3.5 accent-blue-800" />
              Show on public website
            </label>
          </div>
        </div>

        <div className="px-6 pt-5">
          <Field label="Short description (shown on the public website)">
            <Input value={selected.description || ""} onChange={(e) => upd("description", e.target.value)}
              className="h-10" data-testid="course-description"/>
          </Field>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            { term: 1, subs: term1, label: "Term 1" },
            { term: 2, subs: term2, label: "Term 2" },
          ].map(({ term, subs, label }) => (
            <div key={term} className="rounded-lg border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-blue-900">{label}</div>
                <Button size="sm" variant="outline" onClick={() => addSubject(term)}
                  data-testid={`add-subj-t${term}`}>
                  <Plus size={14} className="mr-1"/> Add
                </Button>
              </div>
              <div className="space-y-2">
                {subs.length === 0 && (
                  <div className="text-sm text-slate-400 py-2">No subjects yet.</div>
                )}
                {subs.map((s, i) => (
                  <div key={s.id} className="flex items-center gap-2 group">
                    <div className="flex flex-col text-slate-400">
                      <button className="hover:text-slate-700" onClick={()=>move(s.id,-1)}
                        disabled={i===0} title="Move up">▲</button>
                      <button className="hover:text-slate-700" onClick={()=>move(s.id,1)}
                        disabled={i===subs.length-1} title="Move down">▼</button>
                    </div>
                    <Input
                      value={s.name}
                      onChange={(e) => renameSubject(s.id, e.target.value)}
                      className="h-9"
                      data-testid={`subj-input-${s.id}`}
                    />
                    <button
                      className="p-2 rounded-md text-slate-500 hover:text-red-600 hover:bg-red-50 transition-soft"
                      onClick={() => removeSubject(s.id)}
                      data-testid={`subj-del-${s.id}`}
                      title="Remove"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm text-slate-700">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
